import './App.css';
import './transitions.css';
import React, { useState, useEffect, useRef } from 'react';
import { createTheme, ThemeProvider, CssBaseline, Button, Box, Stack } from '@mui/material';
import { ArrowBack, ArrowForward, LoginSharp, Pause, PlayArrow } from '@mui/icons-material';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { loadConfig, getConfig } from './config';
import api from './services/api';
import Login from './Login';
import DashVendaDia from './components/VendaDia/DashVendaDia';
import DashVendaMes from './components/VendaMes/DashVendaMes';
import DashVendaDiaEmpresa from './components/VendaDiaEmpresa/DashVendaDiaEmpresa';
import DashVendaMesEmpresa from './components/VendaMesEmpresa/DashVendaMesEmpresa';
import DashFaturamentoDia from './components/FaturamentoDia/DashFaturamentoDia';
import DashFaturamentoDiaMesEmpresa from './components/FaturamentoDiaMesEmpresa/DashFaturamentoDiaMesEmpresa';
import DashEmbalagemDia from './components/EmbalagemDia/DashEmbalagemDia';
import DashEmbalagemSemana from './components/EmbalagemSemana/DashEmbalagemSemana';
import DashEmbalagemAtrasada from './components/EmbalagemAtrasada/DashEmbalagemAtrasada';
import DashCarregamentoDia from './components/CarregamentoDia/DashCarregamentoDia';
import DashCarregamentoSemana from './components/CarregamentoSemana/DashCarregamentoSemana';
import DashCarregamentoAtrasado from './components/CarregamentoAtrasado/DashCarregamentoAtrasado';

// Tema escuro
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#17156BFF' },
    background: { default: '#121212', paper: '#1d1d1d' },
    text: { primary: '#ffffff', secondary: '#b0b0b0' },
  },
});

function App() {
  // Estados
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [timerMs, setTimerMs] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Novo estado para verificação inicial
  const [usuarioId, setUsuarioId] = useState(null);
  const [usuarioNome, setUsuarioNome] = useState(null);
  const [componentsList, setComponentsList] = useState([]);
  const [remainingTime, setRemainingTime] = useState(null);

  const intervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const transitionNodeRef = useRef(null);
  const lastSlideTimeRef = useRef(Date.now());

  // Componentes disponíveis
  const componentMap = {
    'vendadia': DashVendaDia,
    'vendames': DashVendaMes,
    'vendadiaempresa': DashVendaDiaEmpresa,
    'vendamesempresa': DashVendaMesEmpresa,
    'faturamentodiames': DashFaturamentoDia,
    'faturamentodiamesempresa': DashFaturamentoDiaMesEmpresa,
    'embalagemdia': DashEmbalagemDia,
    'embalagemsemana': DashEmbalagemSemana,
    'embalagematrasada': DashEmbalagemAtrasada,
    'carregamentodia': DashCarregamentoDia,
    'carregamentosemana': DashCarregamentoSemana,
    'carregamentoatrasado': DashCarregamentoAtrasado,
  };

  // Efeitos
  useEffect(() => {
    const verifyAuth = async () => {
      await checkAuth();
      setIsCheckingAuth(false);
    };
    verifyAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchComponentsList();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, [isPaused, timerMs, isAuthenticated, componentsList]);

  useEffect(() => {
    if (!isPaused && timerMs && isAuthenticated && componentsList.length > 0) {
      lastSlideTimeRef.current = Date.now();
      setRemainingTime(timerMs);
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - lastSlideTimeRef.current;
        setRemainingTime(Math.max(timerMs - elapsed, 0));
      }, 100);
      return () => clearInterval(countdownIntervalRef.current);
    } else {
      clearInterval(countdownIntervalRef.current);
    }
  }, [isPaused, timerMs, isAuthenticated, componentsList, currentIndex]);

  // Funções de autenticação
  const checkAuth = async () => {
    await loadConfig();
    setTimerMs(getConfig('TIMER_MS'));
    setConfigLoaded(true);

    const storedAuth = localStorage.getItem('auth');
    if (!storedAuth) {
      return;
    }

    const authData = JSON.parse(storedAuth);
    const { usuarioId, usuarioNome, expiresAt } = authData;
    setUsuarioId(usuarioId);
    setUsuarioNome(usuarioNome);

    if (expiresAt && new Date() > new Date(expiresAt)) {
      localStorage.removeItem('auth');
      return;
    }

    try {
      const response = await api.post('/verificalogin', { usuarioId, usuarioNome });
      if (response.data.isSuccess) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('auth');
      }
    } catch (error) {
      console.error('Erro ao verificar login:', error);
      localStorage.removeItem('auth');
    }
  };

  const fetchComponentsList = async () => {
    try {
      const response = await api.post('/getdashliberado', { usuarioId });
      setComponentsList(response.data.menus);
    } catch (error) {
      console.error('Erro ao carregar os dashboards:', error);
      setComponentsList([]);
    }
  };

  const handleLoginSuccess = (usuarioId, usuarioNome) => {
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // Expira em 24 horas, ajuste conforme necessário
    const authData = { usuarioId, usuarioNome, expiresAt };
    localStorage.setItem('auth', JSON.stringify(authData));
    setUsuarioId(usuarioId);
    setUsuarioNome(usuarioNome);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth');
    setIsAuthenticated(false);
    setUsuarioId(null);
    setUsuarioNome(null);
    setComponentsList([]);
    clearInterval(intervalRef.current);
  };

  // Funções de navegação e controle
  const startInterval = () => {
    clearInterval(intervalRef.current);
    if (!isPaused && timerMs > 0 && isAuthenticated && componentsList.length > 0) {
      lastSlideTimeRef.current = Date.now();
      intervalRef.current = setInterval(handleNext, timerMs);
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const newIndex = (prev + 1) % componentsList.length;
      lastSlideTimeRef.current = Date.now();
      return newIndex;
    });
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => {
      const newIndex = prev === 0 ? componentsList.length - 1 : prev - 1;
      lastSlideTimeRef.current = Date.now();
      return newIndex;
    });
  };

  const handlePauseResume = () => {
    setIsPaused((prev) => !prev);
  };

  // Renderização de componentes
  const renderComponent = () => {
    if (componentsList.length === 0) {
      return <div>Nenhum dashboard disponível</div>;
    }
    const Component = componentMap[componentsList[currentIndex]] || (() => <div>Componente não encontrado</div>);
    return <Component isPaused={isPaused} setIsPaused={setIsPaused} />;
  };

  // Renderização condicional
  if (isCheckingAuth || !configLoaded || timerMs === null) {
    return <div>Carregando...</div>;
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Login onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    );
  }

  // Renderização principal
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <div className="App">
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Cabeçalho */}
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ color: 'white' }}>Usuário: {usuarioId} - {usuarioNome}</Box>
              <Button variant="contained" color="secondary" onClick={handleLogout} size="small">
                Sair
              </Button>
            </Stack>
          </Box>

          {/* Logos */}
          <Box component="img" src="/LogoEmpresa.png" alt="Logo Empresa" sx={{ position: 'absolute', top: 0, left: 16, height: '80px', zIndex: 1000 }} />
          <Box component="img" src="/LogoTega.png" alt="Logo Tega" sx={{ position: 'absolute', marginTop: '90vh', right: 16, height: '60px', zIndex: 1000, pointerEvents: 'none' }} />

          {/* Transição de componentes */}
          <SwitchTransition mode="out-in">
            <CSSTransition
              nodeRef={transitionNodeRef}
              key={currentIndex}
              timeout={500}
              classNames="fade"
              unmountOnExit>
              <Box ref={transitionNodeRef} sx={{ position: 'absolute', width: '100%', height: '100%', marginTop: 2 }}>
                {renderComponent()}
              </Box>
            </CSSTransition>
          </SwitchTransition>
        </Box>

        {/* Controles */}
        {componentsList.length > 0 && (
          <Box mt={2}>
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
              <Button variant="contained" color="primary" onClick={handlePrevious} startIcon={<ArrowBack />}>
                Anterior
              </Button>
              <Button variant="contained" color={isPaused ? 'success' : 'error'} onClick={handlePauseResume} startIcon={isPaused ? <PlayArrow /> : <Pause />}>
                {isPaused ? 'Retomar' : 'Pausar'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleNext} endIcon={<ArrowForward />}>
                Próximo
              </Button>
            </Stack>
          </Box>
        )}

        {/* Contador de tempo no canto inferior esquerdo */}
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', padding: '8px', borderRadius: '4px' }}>
          {isPaused
            ? 'Pausado'
            : `Troca em: ${(remainingTime / 1000).toFixed(1)} s`}
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;